/**
 * Recharge Click Tracker - Custom Element
 * Detects and tracks all clicks and interactions with Recharge elements
 */

class RechargeTracker extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.CONFIG = {
      selectors: {
        // Radio buttons (ONE-TIME vs SUBSCRIBE)
        radioButtons: '.rc-radio, .onetime-radio, .subscription-radio',
        onetimeRadio: '.rc-radio.onetime-radio',
        subscriptionRadio: '.rc-radio.subscription-radio, .rc-radio--active.subscription-radio',

        // Subscription plan dropdown
        sellingPlanSelect: 'select[name="rc_selling_plan"]',
        sellingPlanLabel: 'label[for*="sellingPlan"]',

        // Details link
        subscriptionDetailsLink: '.rc-subscription-details',

        // Main container
        rechargeWidget: '.rc-widget, [id*="RechargeWidget"]',

        // Button group
        buttonGroup: '.rc-template__button-group, .rc-button-group'
      }
    };

    this.observer = null;
    this.isInitialized = false;
  }

  connectedCallback() {
    // Wait a bit for Recharge to load its elements
    setTimeout(() => {
      this.init();
    }, 1000);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.setupTracking();
    this.observeRechargeChanges();
    // Check initial state
    this.checkCurrentSelection();

    this.isInitialized = true;
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.isInitialized = false;
  }

  setupTracking() {
    // 1. Track clicks on radio buttons (ONE-TIME / SUBSCRIBE)
    this.trackRadioButtons();

    // 2. Track changes in plan dropdown
    this.trackSellingPlanDropdown();

    // 3. Track click on "Subscription details"
    this.trackSubscriptionDetailsLink();

    // 4. Track general clicks on Recharge widget
    this.trackRechargeWidgetClicks();

    // 5. Track variant changes
    this.trackVariantChanges();
  }

  trackRadioButtons() {
    const radioButtons = document.querySelectorAll(this.CONFIG.selectors.radioButtons);

    radioButtons.forEach((radio, index) => {
      // Detect if it's ONE-TIME or SUBSCRIPTION
      const isOnetime = radio.classList.contains('onetime-radio');
      const isSubscription = radio.classList.contains('subscription-radio');
      const type = isOnetime ? 'ONE-TIME' : (isSubscription ? 'SUBSCRIPTION' : 'UNKNOWN');

      // Click listener
      radio.addEventListener('click', (e) => {
        // Check change after a moment
        setTimeout(() => {
          this.checkCurrentSelection();
        }, 100);
      });
    });
  }

  trackSellingPlanDropdown() {
    const dropdown = document.querySelector(this.CONFIG.selectors.sellingPlanSelect);

    if (!dropdown) {
      return;
    }

    // Change listener
    dropdown.addEventListener('change', (e) => {
      // UPDATE the sticky cart immediately after change
      setTimeout(() => {
        this.checkCurrentSelection();
      }, 100);
    });
  }

  trackSubscriptionDetailsLink() {
    const detailsLink = document.querySelector(this.CONFIG.selectors.subscriptionDetailsLink);

    if (!detailsLink) {
      return;
    }
  }

  trackRechargeWidgetClicks() {
    // Track clicks on the entire Recharge widget to detect any interaction
    document.addEventListener('click', (e) => {
      const target = e.target;

      // Check if the click was inside the Recharge widget
      const isRechargeElement = target.closest('.rc-widget') ||
        target.closest('[class*="rc-"]') ||
        target.closest('[id*="Recharge"]');

    });
  }

  trackVariantChanges() {
    // Detect changes in the hidden input that stores the variant ID
    const variantInput = document.querySelector('input[name="id"]');

    if (variantInput) {
      // Use MutationObserver to detect changes in the input value
      const variantObserver = new MutationObserver(() => {
        const newVariantId = variantInput.value;

        this.updateStickyImage(newVariantId);
      });

      variantObserver.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    // Detect changes in all option selects and radios
    const optionSelects = document.querySelectorAll('select[name^="options["]');
    optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        setTimeout(() => {
          const variantInput = document.querySelector('input[name="id"]');
          if (variantInput) {
            this.updateStickyImage(variantInput.value);
          }
        }, 150);
      });
    });

    // Detect changes in radio buttons and option swatches
    const optionInputs = document.querySelectorAll('input[type="radio"][name^="options["]');

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        setTimeout(() => {
          const variantInput = document.querySelector('input[name="id"]');
          if (variantInput) {
            this.updateStickyImage(variantInput.value);
          }
        }, 150);
      });
    });
  }

  updateStickyImage(variantId) {
    const stickyImage = document.querySelector('.sticky-add-to-cart__image-element');

    if (!stickyImage) {
      return;
    }

    // Get the section_id from the sticky cart
    const stickyCart = document.querySelector('.sticky-add-to-cart');
    const sectionId = stickyCart?.getAttribute('data-section-id');

    if (!sectionId) {
      return;
    }

    // Find the JSON variant data generated by Liquid
    const variantDataScript = document.querySelector(`#sticky-variant-data-${sectionId}`);

    if (!variantDataScript) {
      return;
    }

    try {
      const variantData = JSON.parse(variantDataScript.textContent);
      const variant = variantData.variants[variantId];

      if (variant && variant.image && variant.image.src) {
        stickyImage.src = variant.image.src;
        stickyImage.alt = variant.image.alt;
        stickyImage.setAttribute('data-variant-id', variantId);
      }
    } catch (error) {
      console.error('[STICKY IMAGE] Error parsing variant data:', error);
    }
  }

  checkCurrentSelection() {
    let subscriptionText = '';
    let isSubscriptionSelected = false;
    let sellingPlanId = null;

    // Check which radio is active
    const activeRadio = document.querySelector('.rc-radio--active');
    if (activeRadio) {
      const isOnetime = activeRadio.classList.contains('onetime-radio');
      const isSubscription = activeRadio.classList.contains('subscription-radio');
      isSubscriptionSelected = isSubscription;
    }

    // Only look for dropdown if subscription is selected
    if (isSubscriptionSelected) {
      const dropdown = document.querySelector(this.CONFIG.selectors.sellingPlanSelect);
      if (dropdown) {
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        const selectedText = selectedOption?.text || '';
        sellingPlanId = dropdown.value;

        // Extract frequency from text (e.g. "30 day subscription with 25% discount")
        subscriptionText = this.parseSubscriptionText(selectedText);
      } else {
        subscriptionText = 'Deliver every 4 weeks';
      }
    }

    // Update the sticky cart (subscription text and price)
    this.updateStickyCart(isSubscriptionSelected, subscriptionText);
    this.updateStickyPrice(sellingPlanId);
  }

  parseSubscriptionText(text) {
    // Try to get information directly from Recharge dropdown
    const rechargeDropdown = document.querySelector('.rc-selling-plans-dropdown__select, select[name="rc_selling_plan"]');

    if (rechargeDropdown && rechargeDropdown.selectedIndex >= 0) {
      const selectedOption = rechargeDropdown.options[rechargeDropdown.selectedIndex];
      const optionText = selectedOption?.text || '';

      // Use dropdown text directly
      if (optionText) {
        return optionText;
      }
    }

    // Fallback: parse the text passed as parameter
    if (!text) return '';

    // Return the text as it comes from Recharge
    return text;
  }

  updateStickyCart(isSubscription, subscriptionText) {
    const stickySubscriptionElement = document.querySelector('.sticky-add-to-cart__subscription');

    if (!stickySubscriptionElement) {
      return;
    }

    if (isSubscription && subscriptionText) {
      // Show subscription text
      stickySubscriptionElement.textContent = subscriptionText;
      stickySubscriptionElement.style.display = '';
    } else {
      // ONE-TIME purchase - show variant title
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        const variantTitle = this.getVariantTitle(variantInput.value);
        if (variantTitle) {
          stickySubscriptionElement.textContent = variantTitle;
          stickySubscriptionElement.style.display = '';
        } else {
          stickySubscriptionElement.style.display = 'none';
        }
      } else {
        stickySubscriptionElement.style.display = 'none';
      }
    }
  }

  getVariantTitle(variantId) {
    const stickyCart = document.querySelector('.sticky-add-to-cart');
    const sectionId = stickyCart?.getAttribute('data-section-id');
    if (!sectionId) return '';

    const variantDataScript = document.querySelector(`#sticky-variant-data-${sectionId}`);
    if (!variantDataScript) return '';

    try {
      const variantData = JSON.parse(variantDataScript.textContent);
      const variant = variantData.variants[variantId];
      return variant?.title || '';
    } catch (error) {
      return '';
    }
  }

  updateStickyPrice(sellingPlanId) {

    // Get the section_id from the sticky cart
    const stickyCart = document.querySelector('.sticky-add-to-cart');
    const sectionId = stickyCart?.getAttribute('data-section-id');

    if (!sectionId) {
      return;
    }

    // Get current variant ID
    const variantInput = document.querySelector('input[name="id"]');
    if (!variantInput) {
      return;
    }

    const variantId = variantInput.value;

    // Find the JSON data
    const variantDataScript = document.querySelector(`#sticky-variant-data-${sectionId}`);
    if (!variantDataScript) {
      return;
    }

    try {
      const data = JSON.parse(variantDataScript.textContent);
      const variant = data.variants[variantId];

      if (!variant) {
        return;
      }

      let finalPrice = variant.price;
      let comparePrice = variant.compare_at_price;

      // If a selling plan is selected, calculate the discounted price
      if (sellingPlanId && data.selling_plans[sellingPlanId]) {
        const plan = data.selling_plans[sellingPlanId];
        const adjustment = plan.price_adjustments[0]; // Use the first adjustment

        if (adjustment) {
          if (adjustment.value_type === 'percentage') {
            // Percentage discount
            finalPrice = variant.price - (variant.price * adjustment.value / 100);
          } else if (adjustment.value_type === 'fixed_amount') {
            // Fixed discount
            finalPrice = variant.price - adjustment.value;
          } else if (adjustment.value_type === 'price') {
            // Fixed price
            finalPrice = adjustment.value;
          }

          // Original price becomes compare_at_price
          comparePrice = variant.price;
        }
      }

      // Update price HTML
      this.renderStickyPrice(sectionId, finalPrice, comparePrice);

    } catch (error) {
      console.error('[STICKY PRICE] Error updating price:', error);
    }
  }

  renderStickyPrice(sectionId, price, comparePrice) {
    const priceContainer = document.querySelector(`#sticky-price-${sectionId}`);

    if (!priceContainer) {
      return;
    }

    // Format prices (divide by 100 because Shopify stores in cents)
    const formattedPrice = this.formatMoney(price);
    const formattedComparePrice = comparePrice ? this.formatMoney(comparePrice) : null;

    // Create price HTML
    let priceHtml = '<div class="price sticky-price">';

    if (formattedComparePrice && comparePrice > price) {
      // Show discounted price
      priceHtml += `
        <div class="price__container">
          <div class="price__sale">
            <span class="price-item price-item--sale">${formattedComparePrice}</span>
          </div>
          <div class="price__regular">
            <span class="price-item price-item--regular">${formattedPrice}</span>
          </div>
        </div>
      `;
    } else {
      // Show normal price
      priceHtml += `
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${formattedPrice}</span>
          </div>
        </div>
      `;
    }

    priceHtml += '</div>';

    priceContainer.innerHTML = priceHtml;
  }

  formatMoney(cents) {
    // Format cents to dollars with currency format
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  observeRechargeChanges() {
    // Observer to detect when Recharge adds or modifies elements
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Detect if Recharge elements were added
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && (
              node.classList?.contains('rc-widget') ||
              node.querySelector?.('[class*="rc-"]')
            )) {
              setTimeout(() => {
                this.setupTracking();
                this.checkCurrentSelection();
              }, 500);
            }
          });
        }

        // Detect class changes (like rc-radio--active)
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList?.contains('rc-radio')) {

            // UPDATE the sticky cart when radio class changes
            setTimeout(() => {
              this.checkCurrentSelection();
            }, 100);
          }
        }
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class']
    });

  }
}

// Register the custom element
if (!customElements.get('recharge-tracker')) {
  customElements.define('recharge-tracker', RechargeTracker);
}

// Auto-initialize the tracker
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('recharge-tracker')) {
      const tracker = document.createElement('recharge-tracker');
      document.body.appendChild(tracker);
    }
  });
} else {
  if (!document.querySelector('recharge-tracker')) {
    const tracker = document.createElement('recharge-tracker');
    document.body.appendChild(tracker);
  }
}

// Expose global verification function for debugging
window.checkRechargeStatus = () => {
  const tracker = document.querySelector('recharge-tracker');
  if (tracker) {
    tracker.checkCurrentSelection();
  } else {
    console.warn('[RECHARGE TRACKER] Tracker element not found');
  }
};
